import { useEffect, useState } from "react";
import { Copy, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDateTime } from "../../../lib/format";

const TONE = { sent: "bg-brand/20 text-navy", failed: "bg-rose-100 text-rose-700" };
const copy = (text, t) => navigator.clipboard?.writeText(text).then(() => toast.success(t("users.copied"))).catch(() => {});

const Status = ({ s }) => {
  const { t } = useI18n();
  if (!s) return null;
  const k = s.live ? "live" : s.configured ? "off" : "nokey";
  return <span data-testid="email-status" className={`rounded-full px-3 py-1 text-xs font-semibold ${s.live ? "bg-brand/20 text-navy" : "bg-amber-100 text-amber-800"}`}>{t(`x.mail.status.${k}`)}</span>;
};

export const Outbox = ({ mails, onSent }) => {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/users/email-status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  const test = () => {
    setBusy(true);
    api.post("/admin/users/test-email").then((r) => {
      (r.data.status === "sent" ? toast.success : toast.info)(t("x.mail.testDone", { to: r.data.to, status: t(`x.mail.st.${r.data.status}`) }));
      onSent();
    }).catch(() => toast.error(t("common.error"))).finally(() => setBusy(false));
  };
  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm" data-testid="email-outbox">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold text-navy md:text-lg">{t("users.outbox")}</h2>
        <Status s={status} />
        <button type="button" onClick={test} disabled={busy} data-testid="send-test-email" className="ml-auto flex h-11 items-center gap-2 rounded-full bg-brand px-4 text-sm font-semibold text-navy hover:bg-[#8BC21F] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{t("x.mail.test")}
        </button>
      </div>
      <p className="mt-1 text-xs text-slate-500">{t("users.outboxSub")}</p>
      <ul className="mt-3 divide-y divide-slate-100">
        {mails.map((m, i) => (
          <li key={`${m.created_at}-${i}`} className="py-3 text-xs" data-testid="outbox-row">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-500">
              <span>{fmtDateTime(m.created_at)}</span><span className="font-semibold text-navy">{m.to}</span><span>{m.purpose}</span>
              <span className={`rounded-full px-2 py-0.5 font-semibold ${TONE[m.status] || "bg-slate-100 text-slate-600"}`} data-testid="outbox-status">{t(`x.mail.st.${m.status || "simulated"}`)}</span>
            </div>
            <div className="mt-1 font-medium text-navy">{m.subject}</div>
            <div className="mt-0.5 whitespace-pre-line text-ink/80">{m.body}</div>
            {m.error && <div className="mt-1 text-rose-700">{m.error}</div>}
            {m.link && <div className="mt-1 flex items-center gap-2"><span className="truncate text-vblue-ink">{m.link}</span><button type="button" onClick={() => copy(m.link, t)} aria-label="copy" className="grid h-9 w-9 shrink-0 place-items-center rounded-full hover:bg-canvas"><Copy className="h-4 w-4" /></button></div>}
          </li>
        ))}
      </ul>
    </section>
  );
};
